import type { ActionStep, Conditions, FieldDefinition, FieldType } from '@dunea/plugin-sdk';
import httpClient from '~/lib/http-client';

// Owner-facing Automations API (studio `AutomationsOwnerController`).
// `appId` is the CHAT id.

export type RunStatus = 'pending' | 'running' | 'succeeded' | 'failed' | 'skipped';

export interface TriggerField {
  path: string;
  label: string;
  type: FieldType;
  options?: { value: string; label: string }[];
}

export interface AutomationTrigger {
  id: string;
  title: string;
  description: string;
  group: string;
  fields: TriggerField[];
  sample: Record<string, unknown>;
  customerEmail?: string;
  customerName?: string;
  plugin?: { installId: string; slug: string; name: string };
}

export interface AutomationAction {
  id: string;
  title: string;
  description: string;
  input: FieldDefinition[];
  outputs?: { key: string; label: string }[];
  needsCustomer?: boolean;
  needsOrder?: boolean;
  triggers?: string[];
  plugin?: { installId: string; slug: string; name: string; action: string };
  /** Why it can't be used with the trigger asked about. */
  unavailable?: string;
}

export interface AutomationView {
  id: string;
  appId: string;
  name: string;
  enabled: boolean;
  trigger: string;
  triggerTitle: string | null;
  conditions: Conditions;
  actions: ActionStep[];
  source: string;
  runCount: number;
  lastRunAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface AutomationDraft {
  name: string;
  trigger: string;
  conditions: Conditions;
  actions: ActionStep[];
  enabled: boolean;
}

export interface StepResult {
  action: string;
  ok: boolean;
  message?: string;
  output?: Record<string, unknown>;
  at: string;
  continued?: boolean;
}

export interface AutomationRun {
  id: string;
  automationId: string;
  appId: string;
  event: string;
  eventKey: string;
  data: Record<string, unknown>;
  status: RunStatus;
  attempts: number;
  nextAttemptAt: string | null;
  step: number;
  results: StepResult[];
  error: string | null;
  createdAt: string | null;
  finishedAt: string | null;
}

export interface RunPage {
  runs: AutomationRun[];
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface AutomationTemplate {
  id: string;
  title: string;
  description: string;
  icon: string;
  trigger: string;
  params: FieldDefinition[];
}

export interface TestRunResult {
  conditionsPass: boolean;
  data: Record<string, unknown>;
  steps: { action: string; input: Record<string, unknown>; problem?: string }[];
}

const base = (appId: string) => `/api/plugins/automations/apps/${encodeURIComponent(appId)}`;
const seg = encodeURIComponent;

export async function apiListAutomations(appId: string): Promise<AutomationView[]> {
  const { data } = await httpClient.get<AutomationView[]>(base(appId));
  return data;
}

export async function apiCreateAutomation(appId: string, draft: AutomationDraft): Promise<AutomationView> {
  const { data } = await httpClient.post<AutomationView>(base(appId), draft);
  return data;
}

export async function apiUpdateAutomation(
  appId: string,
  id: string,
  patch: Partial<AutomationDraft>,
): Promise<AutomationView> {
  const { data } = await httpClient.patch<AutomationView>(`${base(appId)}/${seg(id)}`, patch);
  return data;
}

export async function apiSetAutomationEnabled(appId: string, id: string, enabled: boolean): Promise<AutomationView> {
  const { data } = await httpClient.post<AutomationView>(
    `${base(appId)}/${seg(id)}/${enabled ? 'enable' : 'disable'}`,
    {},
  );
  return data;
}

export async function apiDeleteAutomation(appId: string, id: string): Promise<void> {
  await httpClient.delete(`${base(appId)}/${seg(id)}`);
}

export async function apiAutomationTriggers(appId: string): Promise<AutomationTrigger[]> {
  const { data } = await httpClient.get<AutomationTrigger[]>(`${base(appId)}/catalog/triggers`);
  return data;
}

export async function apiAutomationActions(appId: string, trigger?: string): Promise<AutomationAction[]> {
  const { data } = await httpClient.get<AutomationAction[]>(
    `${base(appId)}/catalog/actions${trigger ? `?trigger=${seg(trigger)}` : ''}`,
  );
  return data;
}

export async function apiAutomationTemplates(appId: string): Promise<AutomationTemplate[]> {
  const { data } = await httpClient.get<AutomationTemplate[]>(`${base(appId)}/templates`);
  return data;
}

export async function apiAddAutomationTemplate(
  appId: string,
  templateId: string,
  params: Record<string, unknown> = {},
): Promise<AutomationView> {
  const { data } = await httpClient.post<AutomationView>(`${base(appId)}/templates/${seg(templateId)}`, { params });
  return data;
}

export async function apiAutomationRuns(
  appId: string,
  query: { automationId?: string; status?: RunStatus; page?: number; limit?: number } = {},
): Promise<RunPage> {
  const params = new URLSearchParams();

  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== '') {
      params.set(k, String(v));
    }
  }

  const qs = params.toString();
  const { data } = await httpClient.get<RunPage>(`${base(appId)}/runs${qs ? `?${qs}` : ''}`);

  return data;
}

export async function apiAutomationRun(
  appId: string,
  runId: string,
): Promise<AutomationRun & { automation: AutomationView | null }> {
  const { data } = await httpClient.get<AutomationRun & { automation: AutomationView | null }>(
    `${base(appId)}/runs/${seg(runId)}`,
  );
  return data;
}

export async function apiTestAutomation(
  appId: string,
  automation: AutomationDraft,
  data?: Record<string, unknown>,
): Promise<TestRunResult> {
  const res = await httpClient.post<TestRunResult>(`${base(appId)}/test`, { automation, ...(data ? { data } : {}) });
  return res.data;
}

export async function apiAutomationWebhookSecret(appId: string): Promise<{ secret: string | null; header: string }> {
  const { data } = await httpClient.get<{ secret: string | null; header: string }>(`${base(appId)}/webhook-secret`);
  return data;
}
